/* eslint-disable no-unused-expressions */
import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
  useContext,
  memo,
} from 'react'
import Router, { useRouter } from 'next/router'
import classNames from 'classnames'
import { sendTeaCommEvent } from 'lib/tea-analyze'
import useConfig, { IConfig } from './config'
import { childClickHandler, getLink, getAs } from './components/child-wrap'
import { GlobalStoreContext } from 'Components/store'
import { LoginStoreContext } from 'New/login/login-store'
import { useResponsive } from 'Components/hooks/use-responsive'
import Link from 'next/link'

import dynamic from 'next/dynamic'
import useWindowLoaded from 'Components/hooks/use-window-loaded'
const UsedCarWrap = dynamic(() => import('./components/usedcar-modal'), {
  ssr: true,
})
const SelectCarWrap = dynamic(() => import('./components/select-car-wrap'), {
  ssr: false,
})
const ChildWrap = dynamic(() => import('./components/child-wrap'), {
  ssr: false,
})
import { headerTop } from 'Components/new-header'
import usePageOffset, { setPageOffset } from 'Components/hooks/use-page-offset'
import useHasTips from 'Components/hooks/use-top-tips'
import { setAdConfig } from '../home/ad/tianmu-ad/use-ad-config'
import getPageEnterId from 'lib/page-enter'
import useSideNavStatus from 'Components/hooks/use-side-nav-status'
import style from './index.module.scss'
import LightLink from 'Components/new/light-link'

interface IProps {
  isFixed?: boolean
}

export default memo(function SideNav({ isFixed }: IProps) {
  const { asPath } = useRouter()
  const [url, setUrl] = useState(asPath || '')
  const { top } = usePageOffset()

  // 2.0的页面 固定1280宽度 小于1440的时候隐藏
  const isHasTips = useHasTips()
  const responsive = useResponsive()
  const { state: globalStore } = useContext(GlobalStoreContext)
  const { userData } = globalStore
  const { openLogin } = useContext(LoginStoreContext)
  const navStatus = useSideNavStatus()

  const routeChangeComplete = useCallback((url: string) => {
    if (url === '') {
      setUrl('/')
      return
    }
    const asPath = (Router && Router.router && Router.router.asPath) || ''
    const currentPageId = getPageEnterId(asPath).page_id
    const isNoIndex =
      currentPageId !== 'page_main_new' &&
      currentPageId !== 'page_used_car_source_list'
    // 天幕只在首页，因此跳转需要恢复状态
    if (!isHasTips && isNoIndex) {
      setAdConfig({
        homeTopAdShow: false,
      })
      setPageOffset({
        top: 0,
      })
    }
    setUrl(url)
  }, [])

  const isActive = useCallback(
    (as: string | undefined, isActive, isNeedLogin = false) => {
      // 需要处理 url 后面的参数
      let realUrl = url
      if (realUrl?.indexOf('?') > -1) {
        realUrl = realUrl?.split('?')?.[0]
      }
      if (realUrl === as && (!isNeedLogin || userData?.user_id)) {
        return true
      }

      if (isActive && isActive(realUrl, userData?.user_id)) {
        return true
      }
      return false
    },
    [url, userData?.user_id]
  )

  // 判断是否有被选中的主导航，在没有选中的主导航页面，点击主导航需要跳转新标签页
  const router = useRouter()
  const config = useConfig()
  const linkTarget = useMemo(() => {
    if (router.pathname === '/404') {
      return '_self'
    }
    const hasActive = config.some((item) => {
      const itemActive = isActive(item.as, item?.isActive)
      if (itemActive) return true
      return item?.children?.some((child) =>
        isActive(child.as, child?.isActive, child?.isNeedLogin)
      )
    })
    return hasActive ? '_self' : '_blank'
  }, [url, router.pathname, userData?.user_id, config])

  useEffect(() => {
    setUrl(window?.location?.pathname || '/')
    Router.events.on('routeChangeComplete', routeChangeComplete)
    return () => {
      Router.events.off('routeChangeComplete', routeChangeComplete)
    }
  }, [])

  const childClick = useCallback(
    (child: IConfig, e: React.MouseEvent<HTMLElement>) => {
      childClickHandler(child, e, userData?.user_id || '', openLogin)
    },
    [userData?.user_id, openLogin]
  )

  const getAsProxy = useCallback(
    (as?: string, isNeedLogin?: boolean) => {
      return getAs(as, isNeedLogin, userData?.user_id || '')
    },
    [userData?.user_id]
  )

  const isLoaded = useWindowLoaded()

  const isNavRealOff = useMemo(() => {
    return (
      navStatus === 'off' ||
      (navStatus === 'md-off' && !responsive.lg) ||
      (navStatus === 'lg-off' && !responsive.xl)
    )
  }, [navStatus, responsive])
  return (
    <div
      id="sideNavRoot"
      className={classNames(
        style.root,
        { [style['is-off']]: isNavRealOff },
        style[navStatus]
      )}
    >
      <div
        className={classNames(style['fixed-wrapper'], headerTop)}
        style={{ paddingTop: isFixed ? 0 : top }}
      >
        <div className={`${style['scroll-wrapper']} g-hover-scroll-bar`}>
          <ul className={style.list} onClick={(e) => e.stopPropagation()}>
            {config.map((item, index) => {
              return (
                <React.Fragment key={`fragment-${index}`}>
                  <li
                    id={`sideItemWrap_${item.id}`}
                    className={classNames(style['list-item'], `${item.id}`)}
                    key={`side-${index}`}
                  >
                    <LightLink
                      href={getLink(item?.link, item.newVersionLink)}
                      as={getAsProxy(item?.as, item?.isNeedLogin)}
                      prefetch={false}
                    >
                      <a
                        className={classNames(style['item-link'], {
                          [style['is-active']]: isActive(
                            item.as,
                            item?.isActive
                          ),
                          [style['has-children']]: item?.children?.length,
                        })}
                        target={item.target || linkTarget}
                        onClick={(e) => {
                          e.stopPropagation()
                          sendTeaCommEvent('clk_event', {
                            event_id: 'common_menu_item',
                            button_name: item.name,
                          })
                          childClick(item, e)
                        }}
                        onMouseEnter={() => {
                          if (!item.hoverOnOff || isNavRealOff) {
                            item?.onMouseEnter?.()
                          }
                        }}
                        onMouseLeave={() => {
                          if (!item.hoverOnOff || isNavRealOff) {
                            item?.onMouseLeave?.()
                          }
                        }}
                      >
                        {item.icon && (
                          <i
                            className={classNames(
                              `DCD_Icon ${
                                isNavRealOff ? item.foldIcon : item.icon
                              } ${style['link-icon']}`
                            )}
                          />
                        )}
                        <span
                          className={classNames(style['link-name'], {
                            [style['offset']]:
                              item?.children?.length || item.hoverComponent,
                          })}
                        >
                          {isNavRealOff
                            ? item.foldName || item.name
                            : item.name}
                          {(item.hoverComponent || item.children) && (
                            <span
                              className={classNames(
                                style['link-arrow'],
                                'DCD_Icon icon_into_12'
                              )}
                            />
                          )}
                          {item.extraComponent?.(style['rank-flag'])}
                        </span>
                      </a>
                    </LightLink>
                    {item?.children ? (
                      <ul className={style['child-list']}>
                        {item?.children?.map((child: IConfig, i: number) => {
                          return (
                            <li key={`side-child-${i}`}>
                              <LightLink
                                href={getLink(
                                  child?.link,
                                  child.newVersionLink
                                )}
                                as={getAsProxy(child?.as, child?.isNeedLogin)}
                                prefetch={false}
                              >
                                <a
                                  className={classNames(style['child-link'], {
                                    [style['is-active']]: isActive(
                                      child.as,
                                      child?.isActive,
                                      child?.isNeedLogin
                                    ),
                                  })}
                                  target={child.target || linkTarget}
                                  onClick={(e) => {
                                    e.stopPropagation()
                                    sendTeaCommEvent('clk_event', {
                                      event_id: 'common_menu_item',
                                      button_name: child.name,
                                    })
                                    childClick(child, e)
                                  }}
                                >
                                  {child.icon && (
                                    <i
                                      className={`DCD_Icon ${child.icon} ${style['child-link-icon']}`}
                                    />
                                  )}
                                  <span className={style.name}>
                                    {child.name}
                                  </span>
                                </a>
                              </LightLink>
                            </li>
                          )
                        })}
                      </ul>
                    ) : null}
                  </li>
                </React.Fragment>
              )
            })}
          </ul>
        </div>
        {useMemo(() => {
          return isLoaded ? (
            <React.Fragment>
              <SelectCarWrap />
              <UsedCarWrap linkTaget={linkTarget} />
              <ChildWrap />
            </React.Fragment>
          ) : null
        }, [isLoaded])}
      </div>
    </div>
  )
})
